import { useEffect, useRef, useState, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import { Rocket, Users, Calendar, Smile, Trophy, Award } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchAchievements } from "@/lib/api";

const ICONS = { rocket: Rocket, users: Users, calendar: Calendar, smile: Smile, trophy: Trophy, award: Award };

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  // Parse once per value — stable references so the effect doesn't restart.
  const { prefix, target, suffix, hasNum } = useMemo(() => {
    const m = String(value).match(/^([^\d]*)(\d+)(.*)$/);
    return m
      ? { prefix: m[1], target: parseInt(m[2], 10), suffix: m[3], hasNum: true }
      : { prefix: "", target: 0, suffix: "", hasNum: false };
  }, [value]);

  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView || !hasNum) return;
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (now) => {
      const p = Math.min(1, Math.max(0, (now - start) / dur));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, hasNum, target]);

  if (!hasNum) return <span ref={ref}>{value}</span>;
  return (
    <span ref={ref} className="inline-flex items-baseline justify-center" dir="ltr">
      {prefix && <span>{prefix}</span>}
      <span>{n}</span>
      {suffix && <span>{suffix}</span>}
    </span>
  );
}

export default function Achievements() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => { fetchAchievements().then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (items.length === 0) return null;

  return (
    <section className="relative py-20 overflow-hidden" data-testid="achievements-section">
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent, rgba(0,242,96,0.04), transparent)" }} />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal><div className="flex justify-center"><SectionTag>{t.achievements.tag}</SectionTag></div></Reveal>
          <Reveal delay={0.05}><h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{t.achievements.title}</h2></Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((a, i) => {
            const Icon = ICONS[a.icon] || Trophy;
            return (
              <Reveal key={a.id} delay={(i % 4) * 0.1}>
                <motion.div whileHover={{ y: -6 }} className="card-surface rounded-3xl p-8 text-center" data-testid={`achievement-${i}`}>
                  <div className="h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "rgba(0,242,96,0.12)" }}>
                    <Icon size={26} className="text-primary" />
                  </div>
                  <div className="text-4xl sm:text-5xl font-extrabold text-primary glow-text" dir="ltr" style={{ unicodeBidi: "isolate" }}><Counter value={a.value} /></div>
                  <div className="text-slate-400 mt-2 text-sm sm:text-base">{a.label}</div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
